/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Directive, InjectionToken, Input, NgZone, OnDestroy } from '@angular/core';
import { ScheduleTaskType, ScheduleType, TaskRelationType } from '@gh/builder-shared-data';
import {
  DateTimeTypes,
  EMPTY_ENUM_DICTIONARY,
  EnumDictionary,
  LocalizationBusinessService,
  NumberTypes,
  State,
} from '@gh/common-shared';
import { TranslationService } from '@gh/core-mls';
import {
  addMonths,
  aspectAround,
  cloneDate,
  coalesce,
  currentDate,
  currentNormalizedDate,
  isDateValid,
  normalizeDate,
  setHours,
} from '@gh/core-util';
import { combineLatest$ } from '@gh/rx';
import { filter$, skip$ } from '@gh/rx/operators';
import {
  clamp,
  constant,
  find,
  flatMap,
  forEach,
  isDate,
  isNil,
  map,
  pick,
  snakeCase,
} from 'lodash';
import {
  GanttState,
  GanttStateEventType,
  queryGanttAllTasks,
  queryGanttEditable,
  queryGanttEndVisibleDate,
  queryGanttHistoryRestoreVersion,
  queryGanttLeftTrimToPredecessors,
  queryGanttMinDate,
  queryGanttRightTrimToPredecessors,
  queryGanttShowCompletedTasks,
  queryGanttShowMarkedAsDeletedTasks,
  queryGanttStartVisibleDate,
  queryGanttTask,
} from '../gantt-chart.state';
import {
  completionPctFromTimelineTaskProgress,
  GANTT_WORKING_HOURS,
  ganttTaskRelationToTimelineLink,
  ganttTaskToTimelineTask,
  HOURS_END_OF_DAY,
  HOURS_START_OF_DAY,
  isGanttStartTask,
  relationTypeFromTimelineLinkType, WORK_TIME_HOURS_END, WORK_TIME_HOURS_START,
} from '../resources/gantt.model';
import { GanttChartBusService } from '../services/gantt-chart-bus.service';
import { GanttChartLoader } from '../services/gantt-chart-loader.service';
import { DialogService, yesAnswer, yesNoConfirmation } from '@gh/core-ui';
import { GanttChartCommandService } from '../services/gantt-chart-command.service';

export interface GanttChartAccessor {
  onGanttConfig(gantt: GanttStatic): void;

  onGanttReady(gantt: GanttStatic): void;

  onGanttDestroy(gantt: GanttStatic): void;
}

export interface GanttChartController {
  minScale: number;
  maxScale: number;

  setScale(scale: number): void;

  setView(view: GanttChartView): void;
}

export enum GanttChartView {
  MONTH = 'month',
  WEEK = 'week',
  DAY = 'day',
  HOUR = 'hour',
}

export type GanttTooltipRow = {
  field: string;
  title: string;
};

export const GANTT_CHART_ACCESSOR = new InjectionToken<GanttChartAccessor>('GanttChartAccessor');
export const GANTT_CHART_CONTROLLER = new InjectionToken<GanttChartController>('GanttChartController');

const WORKING_MS_IN_DAY = 8 * 60 * 60 * 1000;

@Directive({
  standalone: false,
             selector: '[ghGanttChartTimeline]',
           })
export class GanttChartTimelineDirective implements OnDestroy, GanttChartAccessor {
  @Input() relationshipTypeDict: EnumDictionary<TaskRelationType> = EMPTY_ENUM_DICTIONARY;
  @Input() scheduleType?: ScheduleType;
  @Input() isTemplate?: boolean;
  @Input() isCombined?: boolean;

  gantt: GanttStatic;

  private ganttEl: HTMLElement;
  private accessors: GanttChartAccessor[];

  constructor(
    private dialogs: DialogService,
    private loader: GanttChartLoader,
    private translationService: TranslationService,
    private localizationService: LocalizationBusinessService,
    private bus: GanttChartBusService,
    private commands: GanttChartCommandService,
    private state: State<GanttState>,
    private ngZone: NgZone,
  ) {
  }

  init(ganttEl: HTMLElement): void {
    const { accessors } = this;

    this.ganttEl = ganttEl;

    // verify that dhtmlxgantt library is loaded, otherwise throws an error
    this.loader.verify();

    this.ngZone.runOutsideAngular(() => {
      const gantt = Gantt.getGanttInstance();

      this.gantt = gantt;

      this.onGanttConfig(gantt);

      forEach(accessors, (accessor) => accessor.onGanttConfig(gantt));

      gantt.attachEvent('onGanttReady', () => {
        this.onGanttReady(gantt);
        forEach(accessors, (accessor) => accessor.onGanttReady(gantt));
      });

      // Althougth according to the documentation onGanttScroll event has two parameters (left, top),
      // actually it has 4 parameters (left, oldTop, left, newTop)
      gantt.attachEvent('onGanttScroll', (oldLeft, oldTop, newLeft, newTop) => {
        if (this.bus.suppressScrollSync) {
          return;
        }
        const deltaY = newTop - oldTop;
        const deltaX = newLeft - oldLeft;
        if (deltaY === 0) {
          return;
        }
        this.bus.scroll.next({
                               left  : newLeft,
                               top   : newTop,
                               deltaX,
                               deltaY,
                             });
      });

      gantt.attachEvent('onAfterAutoSchedule', (rootId, updatedIds) => {
        const allUpdatedIds = [rootId].concat(updatedIds);
        this.bus.afterAutoSchedule.next(allUpdatedIds.map((id) => gantt.getTask(id)));
      });

      let showCompleted = true;
      let showDeleted = true;
      gantt.attachEvent('onBeforeDataRender', () => {
        showCompleted = this.state.project(queryGanttShowCompletedTasks);
        showDeleted = this.state.project(queryGanttShowMarkedAsDeletedTasks);
      });

      gantt.attachEvent('onBeforeTaskDisplay', (id, task) => {
        const match = this.bus.isTaskVisibleInSearch(task.id);
        const taskFromState = this.state.value.tasks[task.id];
        const isShow = (showCompleted || task.progress !== 1)
          && (showDeleted || !taskFromState?.markedAsDeleted);

        return isShow && match;
      });

      gantt.attachEvent('onLinkDblClick', (linkId) => {
        const link = gantt.getLink(linkId);
        const source = gantt.getTask(link.source);
        const target = gantt.getTask(link.target);
        const text = `${this.translationService.get('msg_confirm_link_deleting_text')} <b>${source.text}</b> and <b>${target.text}</b>`;
        this.dialogs.confirmation(yesNoConfirmation(text))
            .pipe(filter$(yesAnswer))
            .subscribe(() => {
              this.bus.deleteLinksProgrammatically([linkId]);
              this.bus.afterLinkDelete.next(link);
            });
        return false;
      });

      // Block DHTMLX built-in link deletion; allow only deletes issued through the bus.
      gantt.attachEvent('onBeforeLinkDelete', () => this.bus.allowLinkDelete);

      gantt.attachEvent('onLinkValidation', ({ source, target }) => {
        const sourceTask = this.state.project(queryGanttTask, source);
        const targetTask = this.state.project(queryGanttTask, target);
        // do not allow to create links between tasks coming from different schedules
        return sourceTask.scheduleId === targetTask.scheduleId
          // do not allow to create two links between the same tasks (even if they are of different types)
          && isNil(find(targetTask.predecessors, { taskId: source }))
          && !isGanttStartTask(targetTask)
          && !sourceTask.markedAsDeleted
          && !sourceTask.isExcludeFromGanttChart
          && !targetTask.markedAsDeleted
          && !targetTask.isExcludeFromGanttChart;
      });

      gantt.attachEvent('onAfterLinkAdd', (linkId, link) => {
        this.bus.afterLinkAdd.next(link);
      });
      gantt.attachEvent('onAfterTaskUpdate', (taskId, task) => {
        this.bus.afterTaskUpdate$$.next(task);
      });
      gantt.attachEvent('onTaskSelected', (taskId) => {
        let selectedId = '';
        for (const taskUniqId in this.state.value.tasks) {
          if (this.state.value.tasks[taskUniqId] && this.state.value.tasks[taskUniqId].selected) {
            selectedId = taskUniqId;
            break;
          }
        }
        this.state.mutate({
                            type: GanttStateEventType.SelectTask,
                            taskId,
                          });
        if (selectedId) {
          this.state.mutate({
                              type  : GanttStateEventType.UnselectTask,
                              taskId: selectedId,
                            });
        }
      });
      gantt.attachEvent('onBeforeLightbox', (taskId) => {
        if (!taskId) {
          return false;
        }
        const stateTask = this.state.project(queryGanttTask, taskId);
        if (stateTask.markedAsDeleted) {
          return false;
        }
        if (isGanttStartTask(stateTask) || (stateTask.type !== ScheduleTaskType.CostCenter && stateTask.type !== ScheduleTaskType.Empty)) {
          return false;
        }
        this.commands.openEditTaskDialog(taskId, this.scheduleType, this.isTemplate, this.isCombined);
        return false;
      });

      gantt.attachEvent('onAutoScheduleCircularLink', (cycles: any[]) => {
        // console.log('cycles-->', cycles[0].tasks.map((taskId: string) => this.state.value.tasks[taskId].taskNumber));
      });

      gantt.attachEvent('onBeforeTaskDrag', (id) => {
        const stateTask = this.state.project(queryGanttTask, id);
        if (stateTask.markedAsDeleted) {
          return false;
        }
        return true;
      });
      gantt.attachEvent('onTaskDrag', (id, mode, task, original) => {
        const modes = gantt.config.drag_mode;
        const minDate = this.state.project(queryGanttMinDate);
        if (minDate && (mode === modes.move || mode === modes.resize)) {
          if (task.start_date < minDate) {
            task.start_date = cloneDate(minDate);
            if (mode === modes.move) {
              task.end_date = cloneDate(original.end_date);
            }
          }
        }
        if (mode === modes.resize) {
          // do not allow to make start date before predecessors
          if (task.start_date.getTime() !== original.start_date.getTime()) {
            task.start_date = this.state.project(queryGanttLeftTrimToPredecessors, id, task.start_date);
          }
          // do not allow to make end date after predecessors
          if (task.end_date.getTime() !== original.end_date.getTime()) {
            task.end_date = this.state.project(queryGanttRightTrimToPredecessors, id, task.end_date);
          }
        }
      });
      gantt.addTaskLayer((task: any) => {
        const root = document.createElement('div');
        if (!this.bus.showBaseDates) {
          return root;
        }
        const stateTask: any = this.state.value.tasks[task.id];
        const sizes = gantt.getTaskPosition(task, stateTask.baseStartDate, stateTask.baseEndDate);
        const el = document.createElement('div');
        el.className = 'overlap_bar';
        el.style.left = sizes.left + 'px';
        el.style.width = sizes.width + 'px';
        el.style.height = sizes.height + 'px';
        el.style.top = `${sizes.top + 7}px`;
        el.onmousedown = () => this.gantt.selectTask(task.id);
        el.onmouseenter = () => {
          el.className = 'overlap_bar zero';
          const taskNode = gantt.getTaskNode(task.id);
          taskNode.classList.add('hover');
          taskNode.onmouseleave = () => {
            el.className = 'overlap_bar';
          };
        };
        el.onmouseleave = () => {
          const taskNode = gantt.getTaskNode(task.id);
          taskNode.classList.remove('hover');
        };
        root.appendChild(el);
        return root;
      });
      gantt.init(ganttEl);

      this.applyMonkeyPatches(gantt);

      this.gantt = gantt;

      this.bus.setGanttInstance(gantt);

      this.loadData();
    });

    this.state.select(queryGanttHistoryRestoreVersion).pipe(
      skip$(1),
    ).subscribe(() => this.reloadTimelineFromState());
  }

  private reloadTimelineFromState(): void {
    if (!this.gantt) {
      return;
    }
    const scrollState = this.gantt.getScrollState();
    this.bus.allowAfterLinkSync = false;
    this.gantt.clearAll();
    this.addCurrentDateMarker();
    const tasks = this.state.project(queryGanttAllTasks);
    this.gantt.parse({
                       data : tasks.map(ganttTaskToTimelineTask),
                       links: flatMap(
                         tasks,
                         (task) => map(
                           task.predecessors, (pred) => ganttTaskRelationToTimelineLink(task.taskId, pred)),
                       ),
                     });
    const openedIdList = tasks.filter((task) => task.opened).map((task) => task.taskId);
    this.bus.openTasks(openedIdList);
    this.gantt.scrollTo(scrollState.x, scrollState.y);
    requestAnimationFrame(() => {
      this.bus.allowAfterLinkSync = true;
    });
  }

  ngOnDestroy(): void {
    const { gantt, accessors } = this;

    this.onGanttDestroy(gantt);

    forEach(accessors, (accessor) => accessor.onGanttDestroy(gantt));

    if (gantt) {
      gantt['destructor']();
    }
  }

  setAccessors(accessors: GanttChartAccessor[]): void {
    this.accessors = accessors;
  }

  private addCurrentDateMarker(): void {
    const markerDate = currentDate();
    this.gantt.addMarker({
                           start_date: setHours(
                             normalizeDate(markerDate),
                             clamp(markerDate.getHours(), WORK_TIME_HOURS_START, WORK_TIME_HOURS_END),
                           ),
                           css: 'gantt-today-marker',
                         });
  }
  onGanttConfig(gantt: GanttStatic): void {
    // redefine gantt layout to hide grid
    gantt.config['layout'] = {
      css : 'gantt_container',
      rows: [{
        cols: [{
          // the default timeline view
          view   : 'timeline',
          scrollX: 'scrollHor',
          scrollY: 'scrollVer',
        }, {
          view: 'scrollbar',
          id  : 'scrollVer',
        }],
      }, {
        view: 'scrollbar',
        id  : 'scrollHor',
      }],
    };

    gantt.config.show_errors = false;
    gantt.config.auto_scheduling = true;
    gantt.config.auto_scheduling_strict = true;
    gantt.config.auto_scheduling_initial = false;
    gantt.config.smart_rendering = true;
    gantt.config['smart_scales'] = true;
    gantt.config['drag_project'] = true;
    gantt.config.link_line_width = 1;
    gantt.config.link_arrow_size = 3;
    gantt.config.task_height = 20;
    gantt.config.duration_unit = 'hour';
    gantt.config.duration_step = 8;
    gantt.config.work_time = true;
    gantt.config.select_task = true;
    gantt.config.static_background = true;
    gantt.config.correct_work_time = true;
    gantt.config.round_dnd_dates = true;
    gantt.config.details_on_dblclick = true;
    gantt.config.time_step = 60;
    gantt.config.row_height = 35;
    gantt.config.min_column_width = 30;

    this.setupTaskTemplate();
    this.setupLinkDescriptionTemplate();
    this.addCurrentDateMarker();
  }

  onGanttReady(gantt: GanttStatic): void {
    // this.ganttEl.querySelector('.gantt_task_vscroll')!.addEventListener('scroll', (event) => {
    //   const { scrollLeft, scrollTop } = <HTMLElement>event.target;
    //   this.bus.scroll.next({ left: scrollLeft, top: scrollTop });
    // });
  }

  onGanttDestroy(gantt: GanttStatic): void {
    // nothing to do
  }

  setupTooltipTemplate(rows: GanttTooltipRow[]): void {
    const taskFields = rows.map(({ field }) => field);
    const taskTooltipEl = this.buildTooltip(rows);
    const taskTooltipNameEl = document.createElement('div');
    taskTooltipNameEl.id = 'name';
    taskTooltipNameEl.className = 'gantt_tooltip_title';
    taskTooltipEl.insertBefore(taskTooltipNameEl, taskTooltipEl.firstChild);

    const linkTooltipEl = this.buildTooltip([
                                              { field: 'taskLink', title: 'lbl_task_link' },
                                              { field: 'lag', title: 'lbl_lag' },
                                              { field: 'from', title: 'lbl_from' },
                                              { field: 'to', title: 'lbl_to' },
                                            ]);

    this.gantt.templates.tooltip_text = <any>((start: Date, end: Date, object: any, objectType: string) => {
      switch (objectType) {
        case 'task': {
          const progress = NumberTypes.Percents.format(completionPctFromTimelineTaskProgress(object.progress || 0));
          const duration = this.localizationService.formatDaysDuration(object.duration);
          const tooltipValues = pick(
            {
              name       : object.name,
              duration,
              description: object.description,
              startDate  : DateTimeTypes.ShortDate.format(start),
              endDate    : DateTimeTypes.ShortDate.format(end),
              progress   : `${progress}%`,
            },
            taskFields,
          );

          taskTooltipNameEl.innerText = object.name;
          Object
          .keys(tooltipValues)
          .forEach((prop) => taskTooltipEl.querySelector(`#${prop}`)!.innerHTML = tooltipValues[prop] || '');

          return taskTooltipEl.innerHTML;
        }
        case 'link': {
          const tooltipValues = {
            taskLink: this.relationshipTypeDict.get(relationTypeFromTimelineLinkType(object.type)),
            lag     : object.lag || 0,
            from    : this.gantt.getTask(object.source).name,
            to      : this.gantt.getTask(object.target).name,
          };

          Object
          .keys(tooltipValues)
          .forEach((prop) => linkTooltipEl.querySelector(`#${prop}`)!.innerHTML = coalesce(tooltipValues[prop], ''));

          return linkTooltipEl.innerHTML;
        }
        default:
          return '';
      }
    });
  }

  setAllWorkingDays(): void {
    const { gantt } = this;

    gantt.setWorkTime({
                        hours: [8, 16],
                        days : [1, 1, 1, 1, 1, 1, 1],
                      });
    gantt.setWorkTime({
                        hours: [8, 16],
                        day  : 0,
                      });
    gantt.setWorkTime({
                        hours: [8, 16],
                        day  : 6,
                      });
  }

  addClass(className: string): void {
    this.ganttEl.classList.add(className);
  }

  removeClass(className: string): void {
    this.ganttEl.classList.remove(className);
  }

  showDate(date: Date): void {
    this.gantt.showDate(date);
  }

  private setupLinkDescriptionTemplate(): void {
    const descriptionEl = this.buildTooltip([
                                              { field: 'from', title: 'lbl_from' },
                                              { field: 'to', title: 'lbl_to' },
                                            ]);

    this.gantt.templates.drag_link = (from: string, fromStart: boolean, to: string, toStart: boolean) => {
      const taskFrom = this.gantt.getTask(from);

      const descriptionValues: any = {
        from: taskFrom.name,
      };

      if (to) {
        const taskTo = this.gantt.getTask(to);
        descriptionValues.to = taskTo.name;
      }

      Object
      .keys(descriptionValues)
      .forEach((prop) => descriptionEl.querySelector(`#${prop}`)!.innerHTML = descriptionValues[prop] || '');

      return descriptionEl.innerHTML;
    };
  }

  private setupTaskTemplate(): void {
    this.gantt.templates.task_class = (start: Date, end: Date, task: any) => !task.parent ? 'gantt_task_parent' : '';
  }

  private buildTooltip(rows: GanttTooltipRow[]): HTMLElement {
    const tooltipEl = document.createElement('div');
    const tooltipClassPrefix = 'gantt_tooltip';
    tooltipEl.className = `${tooltipClassPrefix}_container`;

    const props = rows.map(({ field }) => field);
    props.forEach((prop) => {
      const tooltipPropEl = document.createElement('div');
      tooltipPropEl.className = `${tooltipClassPrefix}_row`;

      const tooltipPropLabelEl = document.createElement('label');
      tooltipPropLabelEl.className = `${tooltipClassPrefix}_label`;
      tooltipPropLabelEl.innerHTML = this.translationService.getRaw(`lbl_${snakeCase(prop)}`);
      tooltipPropEl.appendChild(tooltipPropLabelEl);

      const tooltipPropValueEl = document.createElement('span');
      tooltipPropValueEl.id = prop;
      tooltipPropValueEl.className = `${tooltipClassPrefix}_value ${tooltipClassPrefix}_value_${prop}`;
      tooltipPropEl.appendChild(tooltipPropValueEl);

      tooltipEl.appendChild(tooltipPropEl);
    });

    return tooltipEl;
  }

  private loadData(): void {
    const tasks = this.state.project(queryGanttAllTasks);
    const editable = this.state.project(queryGanttEditable);
    const { start: startDate, end: endDate } = this.safeGanttConfigVisibleRange();

    this.gantt.config.readonly = !editable;
    this.gantt.config.start_date = startDate;
    this.gantt.config.end_date = endDate;
    this.gantt.parse({
                       data : tasks.map(ganttTaskToTimelineTask),
                       links: flatMap(
                         tasks,
                         (task) => map(task.predecessors, (pred) => ganttTaskRelationToTimelineLink(task.taskId, pred)),
                       ),
                     });

    combineLatest$(this.state.select(queryGanttStartVisibleDate), this.state.select(queryGanttEndVisibleDate))
    .pipe(
      // skip first event, because we already initialized gantt chart
      skip$(1))
    .subscribe(
      ([nextStartDate, nextEndDate]) => {
        const { start, end } = this.safeGanttConfigVisibleRangeFrom(nextStartDate, nextEndDate);
        const currentStart = this.gantt.config.start_date?.getTime();
        const currentEnd = this.gantt.config.end_date?.getTime();
        if (currentStart === start.getTime() && currentEnd === end.getTime()) {
          return;
        }
        this.gantt.config.start_date = start;
        this.gantt.config.end_date = end;
        this.gantt.render();
      });
  }

  private safeGanttConfigVisibleRange(): { start: Date; end: Date } {
    const startRaw = this.state.project(queryGanttStartVisibleDate);
    const endRaw = this.state.project(queryGanttEndVisibleDate);
    return this.safeGanttConfigVisibleRangeFrom(startRaw, endRaw);
  }

  private safeGanttConfigVisibleRangeFrom(startRaw: Date | undefined, endRaw: Date | undefined): { start: Date; end: Date } {
    const start = isDate(startRaw) && isDateValid(startRaw) ? startRaw : currentNormalizedDate();
    let end = isDate(endRaw) && isDateValid(endRaw) ? endRaw : addMonths(start, 1);
    if (end.getTime() <= start.getTime()) {
      end = addMonths(start, 1);
    }
    return { start, end };
  }

  private applyMonkeyPatches(gantt: any): void {
    // we patch horizontal ScrollbarCell to guarantee that it is always shown
    const horizontalScrollbarCell = gantt.$layout.$cells[1].$content;
    horizontalScrollbarCell.shouldShow = constant(true);
    horizontalScrollbarCell.shouldHide = constant(false);

    // we patch initScaleConfig to update start time of each day to 8:00 AM
    const scaleHelper = gantt.$layout.$cells[0].$cells[0].$content.$scaleHelper;
    const originalInitScaleConfig = scaleHelper.initScaleConfig;
    scaleHelper.initScaleConfig = function(scale: any, minDate: any, maxDate: any): any {
      const min = minDate instanceof Date && !isNaN(minDate.getTime()) ? minDate : currentNormalizedDate();
      let max = maxDate instanceof Date && !isNaN(maxDate.getTime()) ? maxDate : addMonths(min, 1);
      if (max.getTime() <= min.getTime()) {
        max = addMonths(min, 1);
      }
      const cfg = originalInitScaleConfig.call(this, scale, min, max);
      if (scale.unit !== 'hour' && cfg.trace_x && cfg.trace_x.length) {
        cfg.trace_x.forEach((date: Date) => {
          if (date && !isNaN(date.getTime())) {
            date.setHours(GANTT_WORKING_HOURS);
          }
        });
      }
      return cfg;
    };

    const originalLimitVisibleRange = scaleHelper.limitVisibleRange;
    scaleHelper.limitVisibleRange = function(cfg: any): void {
      const dates = cfg && cfg.trace_x;
      if (!dates || !dates.length) {
        return;
      }
      const last = dates[dates.length - 1];
      if (!last || !isDateValid(last)) {
        return;
      }
      return originalLimitVisibleRange.call(this, cfg);
    };

    // we have to ensure that duration of day is 8 working hours
    const timeline = gantt.$layout.$cells[0].$cells[0].$content;
    const originalGetColumnDuration = timeline._getColumnDuration;
    timeline._getColumnDuration = function(scale: any, date: Date): number {
      if (scale.unit === 'day') {
        return WORKING_MS_IN_DAY;
      } else {
        return originalGetColumnDuration.call(this, scale, date);
      }
    };

    // we patch roundDate method to round dates to the beginning/end of a day for HOURS view
    aspectAround(
      gantt,
      'roundDate',
      (original) =>
        function(config: any): Date {
          if (!isDate(config)) {
            const { date, unit, step } = config;
            if (unit === 'minute' && step < 0) {
              const lower = setHours(normalizeDate(date), HOURS_START_OF_DAY);
              const upper = setHours(normalizeDate(date), HOURS_END_OF_DAY);
              if (Math.abs(date.getTime() - lower.getTime()) < Math.abs(date.getTime() - upper.getTime())) {
                return lower;
              } else {
                return upper;
              }
            }
          }
          return original.call(this, config);
        },
    );
  }
}
